import React, { useState } from 'react';
import { Header } from './components/Header';
import { Navigation } from './components/Navigation';
import { Dashboard } from './components/Dashboard';
import { CardView } from './components/CardView';
import { SocialFeed } from './components/SocialFeed';
import { UserProfile } from './components/UserProfile';
import { ThemeProvider } from './components/ThemeContext';
import { GiftAidBanner } from './components/GiftAidBanner';
import { GiftAidDetails } from './components/GiftAidDetails';
import { OpenBankingConnect } from './components/OpenBankingConnect';
import { AccountSummary } from './components/IncomeManagement';
import { AccountLinkingManager } from './components/AccountLinkingManager';
import { HabitatDetails } from './pages/HabitatDetails';
import { DebtManagement } from './pages/DebtManagement';
import { LoginScreen } from './components/LoginScreen';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
// Mock data - replace with actual data from your backend/state management
const accountData = {
  currentBalance: 4247.82,
  emergencyFundBalance: 5500,
  monthlyIncome: 4500,
  incomeHistory: [{
    date: '2024-01-31',
    amount: 4500,
    source: 'Salary'
  }, {
    date: '2024-02-29',
    amount: 4500,
    source: 'Salary'
  }, {
    date: '2024-03-31',
    amount: 4500,
    source: 'Salary'
  }],
  monthlyPayments: [{
    name: 'Rent',
    amount: 1500,
    date: '1st'
  }, {
    name: 'Council Tax',
    amount: 150,
    date: '1st'
  }, {
    name: 'Utilities',
    amount: 200,
    date: '5th'
  }, {
    name: 'Transport',
    amount: 150,
    date: '10th'
  }, {
    name: 'Phone',
    amount: 45,
    date: '15th'
  }, {
    name: 'Groceries',
    amount: 400,
    date: 'Various'
  }, {
    name: 'Insurance',
    amount: 100,
    date: '15th'
  }, {
    name: 'Gym Membership',
    amount: 50,
    date: '20th'
  }],
  detectedIncome: {
    amount: '4500',
    lastPaymentDate: new Date('2024-03-31'),
    rhythm: 'steady' as const,
    employer: 'Tech Solutions Ltd'
  }
};
// Mock linked accounts data
const linkedAccountsData = [{
  id: 'acc1',
  bankName: 'HSBC',
  accountNumber: '12345678',
  sortCode: '40-47-84',
  balance: 2450.5,
  accountType: 'current' as const,
  isAuthorized: true,
  directDebits: [{
    id: 'dd1',
    name: 'Netflix',
    amount: 15.99,
    frequency: 'monthly' as const,
    nextPaymentDate: '2024-02-15',
    category: 'Entertainment'
  }, {
    id: 'dd2',
    name: 'Spotify',
    amount: 9.99,
    frequency: 'monthly' as const,
    nextPaymentDate: '2024-02-10',
    category: 'Entertainment'
  }]
}, {
  id: 'acc2',
  bankName: 'Barclays',
  accountNumber: '87654321',
  sortCode: '20-00-00',
  balance: 5780.25,
  accountType: 'savings' as const,
  isAuthorized: true,
  directDebits: []
}];
export function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [cardShippedNotification, setCardShippedNotification] = useState(false);
  const handleLogin = () => {
    setIsAuthenticated(true);
  };
  const handleAlertClick = (paymentId: string) => {
    setCurrentView('dashboard');
    setSelectedPaymentId(paymentId);
  };
  const handleCardShipped = () => {
    setCardShippedNotification(true);
  };
  const renderView = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard selectedPaymentId={selectedPaymentId} onPaymentManaged={() => setSelectedPaymentId(null)} onCardShipped={handleCardShipped} data-id="element-0" />;
      case 'cards':
        return <CardView data-id="element-1" />;
      case 'social':
        return <SocialFeed data-id="element-2" />;
      case 'profile':
        return <UserProfile data-id="element-3" />;
      case 'openbanking':
        return <OpenBankingConnect data-id="element-4" />;
      default:
        return <Dashboard selectedPaymentId={selectedPaymentId} onPaymentManaged={() => setSelectedPaymentId(null)} onCardShipped={handleCardShipped} data-id="element-5" />;
    }
  };
  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} data-id="element-6" />;
  }
  return <BrowserRouter data-id="element-7">
      <ThemeProvider data-id="element-8">
        <div className="min-h-screen bg-background dark:bg-gray-900 flex flex-col transition-colors duration-200" data-id="element-9">
          <Routes data-id="element-10">
            <Route path="/" element={<>
                  <Header onAlertClick={handleAlertClick} cardShippedNotification={cardShippedNotification} data-id="element-12" />
                  <GiftAidBanner data-id="element-13" />
                  <main className="flex-1 flex flex-col md:flex-row" data-id="element-14">
                    <Navigation currentView={currentView} setCurrentView={setCurrentView} data-id="element-15" />
                    <div className="flex-1 p-4 md:p-6 overflow-y-auto" data-id="element-16">
                      {renderView()}
                    </div>
                  </main>
                </>} data-id="element-11" />
            <Route path="/giftaid" element={<>
                  <Header onAlertClick={handleAlertClick} cardShippedNotification={cardShippedNotification} data-id="element-18" />
                  <GiftAidBanner data-id="element-19" />
                  <main className="flex-1 flex flex-col md:flex-row" data-id="element-20">
                    <Navigation currentView={currentView} setCurrentView={setCurrentView} data-id="element-21" />
                    <div className="flex-1 p-4 md:p-6 overflow-y-auto" data-id="element-22">
                      <GiftAidDetails data-id="element-23" />
                    </div>
                  </main>
                </>} data-id="element-17" />
            <Route path="/openbanking" element={<>
                  <Header onAlertClick={handleAlertClick} cardShippedNotification={cardShippedNotification} data-id="element-25" />
                  <GiftAidBanner data-id="element-26" />
                  <main className="flex-1 flex flex-col md:flex-row" data-id="element-27">
                    <Navigation currentView="openbanking" setCurrentView={setCurrentView} data-id="element-28" />
                    <div className="flex-1 p-4 md:p-6 overflow-y-auto" data-id="element-29">
                      <OpenBankingConnect data-id="element-30" />
                    </div>
                  </main>
                </>} data-id="element-24" />
            <Route path="/income" element={<>
                  <Header onAlertClick={handleAlertClick} cardShippedNotification={cardShippedNotification} data-id="element-32" />
                  <GiftAidBanner data-id="element-33" />
                  <main className="flex-1 flex flex-col md:flex-row" data-id="element-34">
                    <Navigation currentView="income" setCurrentView={setCurrentView} data-id="element-35" />
                    <div className="flex-1 p-4 md:p-6 overflow-y-auto" data-id="element-36">
                      <AccountSummary currentBalance={accountData.currentBalance} emergencyFundBalance={accountData.emergencyFundBalance} monthlyIncome={accountData.monthlyIncome} incomeHistory={accountData.incomeHistory} monthlyPayments={accountData.monthlyPayments} detectedIncome={accountData.detectedIncome} data-id="element-37" />
                    </div>
                  </main>
                </>} data-id="element-31" />
            <Route path="/linked-accounts" element={<>
                  <Header onAlertClick={handleAlertClick} cardShippedNotification={cardShippedNotification} data-id="element-39" />
                  <GiftAidBanner data-id="element-40" />
                  <main className="flex-1 flex flex-col md:flex-row" data-id="element-41">
                    <Navigation currentView="linked-accounts" setCurrentView={setCurrentView} data-id="element-42" />
                    <div className="flex-1 p-4 md:p-6 overflow-y-auto" data-id="element-43">
                      <AccountLinkingManager linkedAccounts={linkedAccountsData} onLinkNewAccount={() => console.log('Link new account')} onTransferComplete={id => console.log('Transfer complete:', id)} data-id="element-44" />
                    </div>
                  </main>
                </>} data-id="element-38" />
            <Route path="/debt-management" element={<>
                  <Header onAlertClick={handleAlertClick} cardShippedNotification={cardShippedNotification} data-id="element-46" />
                  <GiftAidBanner data-id="element-47" />
                  <main className="flex-1 flex flex-col md:flex-row" data-id="element-48">
                    <Navigation currentView="debt-management" setCurrentView={setCurrentView} data-id="element-49" />
                    <div className="flex-1 overflow-y-auto" data-id="element-50">
                      <DebtManagement data-id="element-51" />
                    </div>
                  </main>
                </>} data-id="element-45" />
            <Route path="/habitat-details" element={<HabitatDetails data-id="element-53" />} data-id="element-52" />
            <Route path="*" element={<Navigate to="/" replace data-id="element-55" />} data-id="element-54" />
          </Routes>
        </div>
      </ThemeProvider>
    </BrowserRouter>;
}